import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Eraser, TriangleAlert } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { fmtDateFull } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function StartFreshPanel() {
  const qc = useQueryClient();
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);
  const { data: p } = useQuery({ queryKey: ["start-fresh-preview"], queryFn: () => api.get("/maintenance/start-fresh/preview").then((r) => r.data) });
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/maintenance/start-fresh", { confirm: phrase });
      toast.success(`Done — removed ${data.removed.tickets} ticket(s) and ${data.removed.users} demo user(s). Ticket numbering restarts at TT-0001.`);
      setPhrase("");
      qc.invalidateQueries();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const nothing = p && p.counts.tickets === 0 && p.demo_users.length === 0 && p.counts.backups === 0;
  return (
    <div className="surface border-red-500/40 p-4" data-testid="start-fresh-panel">
      <div className="flex items-center gap-2 text-sm font-bold"><Eraser className="h-4 w-4 text-red-500" /> Remove sample data & start fresh</div>
      <p className="mt-1 text-xs text-muted-foreground">One-time reset before real use. Deletes <strong>every ticket</strong> (with messages, history and attachments), all alert / inbound / turned-away logs, report history, the mail outbox and every snapshot, plus any demo users (<span className="mono">@example.com</span>). Keeps real users, channels, rules, saved reports and settings. Ticket numbering restarts at TT-0001.</p>
      {p && (
        <div className="mono mt-3 grid grid-cols-3 gap-px bg-border text-center text-xs sm:grid-cols-6" data-testid="start-fresh-counts">
          {Object.entries(p.counts).map(([k, v]) => <div key={k} className="bg-card px-2 py-1.5"><div className="text-sm font-semibold">{v}</div><div className="eyebrow">{k.replace("_", " ")}</div></div>)}
        </div>
      )}
      {p?.demo_users?.length > 0 && <p className="mt-2 text-[11px] text-muted-foreground">Demo users to remove: {p.demo_users.map((u) => u.name).join(", ")}</p>}
      {p?.last_done_at && <p className="mt-2 text-[11px] text-emerald-500" data-testid="start-fresh-done">Last run {fmtDateFull(p.last_done_at)}</p>}
      {!nothing && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Input value={phrase} onChange={(e) => setPhrase(e.target.value)} placeholder={`Type ${p?.phrase || "START FRESH"} to confirm`} className="mono h-9 w-64 text-xs" data-testid="start-fresh-input" />
          <Button variant="destructive" disabled={busy || phrase.trim().toUpperCase() !== (p?.phrase || "START FRESH")} onClick={run} data-testid="start-fresh-btn"><TriangleAlert className="mr-1.5 h-4 w-4" /> {busy ? "Removing…" : "Remove sample data"}</Button>
        </div>
      )}
      {nothing && <p className="mt-3 text-xs text-emerald-500" data-testid="start-fresh-clean">Nothing to remove — this system holds no tickets, snapshots or demo users.</p>}
    </div>
  );
}
